import { useState, useEffect } from "react";
import { ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, SortingState, useReactTable } from "@tanstack/react-table";
import { MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import Pagination from "@/common/Table/ItemsPerPage";
import moment from "moment";
import TruncatedToolTipChildren from "@/common/ui/TruncatedToolTipChildren";
import Checkbox from "@/common/custom/Chekbox";
import { BsArrowUp, BsArrowDown } from "react-icons/bs";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { LightText2 } from "@/common/ui/Text";
import EmptyList from "@/components/common/EmptyList";

interface PaymentHistoryTableDropdownOption<T> {
  label: string;
  onClickFun: (data: T) => void;
  type?: string;
  show?: (row: T) => boolean;
}

interface PaymentHistoryTableProps<T> {
  className?: string;
  data: T[];
  columnsConfig: ColumnDef<T>[];
  columnClassName?: string;
  rowSelection: any;
  setRowSelection: any;
  isSelectAllActive?: boolean;
  setIsSelectAllActive?: (active: boolean) => void;
  openModal: (modalKey: string, props?: any) => void;
  paginationConfig?: {
    initialPage?: number;
    initialPerPage?: number;
    perPageOptions?: number[];
  };
  dropdownOptions: PaymentHistoryTableDropdownOption<T>[];
  hidePagination?: boolean;
  enableRowSelection?: boolean;
  enableMoreBtn?: boolean;
  onPageChange?: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  totalCount?: number;
  currentPage?: number;
  currentPerPage?: number;
  enableSorting?: boolean;
  sortBy?: Array<{ field: string; isASC: boolean }>;
  onSortChange?: (sortBy: Array<{ field: string; isASC: boolean }>) => void;
  emptyStateText?: string;
  emptyStateIcon?: string;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

const PaymentHistoryTable = <T extends { id?: string }>({
  className = "",
  data,
  columnsConfig,
  dropdownOptions,
  rowSelection,
  setRowSelection,
  isSelectAllActive = false,
  setIsSelectAllActive,
  columnClassName = "max-w-[170px] w-[170px] min-w-[170px]",
  paginationConfig = { initialPage: 1, initialPerPage: 25, perPageOptions: [10, 25, 50, 100] },
  hidePagination = false,
  enableRowSelection = false,
  enableMoreBtn = false,
  onPageChange,
  onPerPageChange,
  totalCount,
  currentPage: externalCurrentPage,
  currentPerPage: externalCurrentPerPage,
  enableSorting = false,
  sortBy = [],
  onSortChange,
  emptyStateText = "No data available",
  emptyStateIcon = "/icons/empty-list-file.svg",
  isLoading = false,
  error = null,
  onRetry,
}: PaymentHistoryTableProps<T>) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [currentPage, setCurrentPage] = useState(paginationConfig.initialPage!);
  const [perPage, setPerPage] = useState({ value: paginationConfig.initialPerPage!, label: `Records per page ${paginationConfig.initialPerPage!}` });

  // Sync internal state with external props
  useEffect(() => {
    if (externalCurrentPage !== undefined) {
      setCurrentPage(externalCurrentPage);
    }
  }, [externalCurrentPage]);

  useEffect(() => {
    if (externalCurrentPerPage !== undefined) {
      setPerPage({ value: externalCurrentPerPage, label: `Records per page ${externalCurrentPerPage}` });
    }
  }, [externalCurrentPerPage]);

  // Sync sorting state with external sortBy prop
  useEffect(() => {
    if (enableSorting && sortBy.length > 0) {
      const tanstackSorting = sortBy.map((sort) => ({
        id: sort.field,
        desc: !sort.isASC,
      }));
      setSorting(tanstackSorting);
    }
  }, [sortBy, enableSorting]);

  // Handle sorting changes
  const handleSortingChange = (updaterOrValue: any) => {
    setSorting(updaterOrValue);
    
    if (enableSorting && onSortChange) {
      const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
      const sortByArray = newSorting.map((sort: any) => ({
        field: sort.id,
        isASC: !sort.desc,
      }));
      onSortChange(sortByArray);
    }
  };

  // Define numeric fields that should show 0-9/9-0 sorting
  const numericFields = ['emp_id', 'document_price', 'transaction_id', 'proof_id'];
  
  // Helper function to check if a field is numeric
  const isNumericField = (fieldName: string) => {
    return numericFields.includes(fieldName);
  };

  // Create checkbox column if row selection is enabled
  const selectColumn: ColumnDef<T> = {
    id: "select",
    header: ({ table }) => {
      if (!enableRowSelection) return null;
      const allRowsSelected = table.getIsAllRowsSelected();
      const someRowsSelected = table.getIsSomeRowsSelected();

      const handleSelectAll = () => {
        if (isSelectAllActive) {
          // If select all is active, deactivate it and clear selections
          setIsSelectAllActive?.(false);
          setRowSelection({});
        } else {
          // Activate select all mode - this selects ALL records across all pages
          setIsSelectAllActive?.(true);
          setRowSelection({});
        }
      };

      return (
        <div className="flex items-center justify-end">
          <Checkbox
            checked={isSelectAllActive}
            onChange={handleSelectAll}
            className={someRowsSelected && !allRowsSelected && !isSelectAllActive ? "opacity-60" : ""}
          />
        </div>
      );
    },
    cell: ({ row }) => {
      if (!enableRowSelection) return null;
      
      const handleRowSelection = () => {
        if (isSelectAllActive) {
          // If select all is active, deactivate it and select all rows except this one
          setIsSelectAllActive?.(false);
          const allRowIds = data.reduce((acc: any, _, index: number) => {
            if (index.toString() !== row.id) {
              acc[index.toString()] = true;
            }
            return acc;
          }, {});
          setRowSelection(allRowIds);
        } else {
          // Normal row selection
          row.toggleSelected(!row.getIsSelected());
        }
      };

      // Filter dropdown options to check if any are visible for this row
      const visibleOptions = dropdownOptions.filter((option) => 
        typeof option.show === "function" ? option.show(row.original) : true
      );

      return (
        <div className="flex items-center justify-between w-full px-1">
          <div className="flex-1">
            {visibleOptions.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4 text-text-default" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {visibleOptions.map((option, index) => (
                    <DropdownMenuItem
                      key={index}
                      onClick={() => option.onClickFun(row.original)}
                      className={`cursor-pointer px-3 py-2 text-13 ${
                        option.type === "remove"
                          ? "text-text-error data-[highlighted]:text-text-error"
                          : "text-text-default data-[highlighted]:text-text-primary"
                      } data-[highlighted]:bg-primaryOpacity-10 focus:bg-primaryOpacity-10 focus:text-text-primary`}
                    >
                      {option.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          <Checkbox 
            checked={isSelectAllActive || row.getIsSelected()} 
            onChange={handleRowSelection} 
          />
        </div>
      );
    },
    size: 80,
    minSize: 80,
    maxSize: 80,
    enableResizing: false,
  };

  // Combine columns with checkbox column if enabled
  const finalColumns = enableRowSelection ? [selectColumn, ...columnsConfig] : columnsConfig;

  const table = useReactTable({
    data: data,
    columns: finalColumns,
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: enableRowSelection,
    enableSorting: enableSorting,
    state: {
      sorting,
      rowSelection,
    },
  });

  // Loading state
  if (isLoading) {
    return (
      <div className={`${className} flex flex-col h-full`}>
        <div className="flex-1 overflow-y-auto">
          <Table className="border-separate border-spacing-0 w-full h-[100%]">
            <TableHeader className="sticky top-0 z-30 bg-white whitespace-nowrap">
              <TableRow className="w-fit">
                 {!enableRowSelection && enableMoreBtn && (
                   <TableHead className="!w-[40px] !max-w-[40px] !min-w-[40px] 2xl:!w-[30px] 2xl:max-w-[30px] 2xl:min-!w-[30px] border-b border-r border-t border-stroke-light sticky left-0 bg-white z-50" />
                 )}
                {table.getHeaderGroups().map((headerGroup) =>
                  headerGroup.headers.map((header) => {
                    const isSelectColumn = header.column.id === "select";
                    const canSort = enableSorting && !isSelectColumn && header.column.getCanSort();
                    const sortInfo = sortBy.find((s: any) => s.field === header.column.id);
                    const isActiveAsc = !!(sortInfo && sortInfo.isASC === true);
                    const isActiveDesc = !!(sortInfo && sortInfo.isASC === false);

                    const handleSortToggle = (isASC: boolean) => {
                      if (sortInfo && sortInfo.isASC === isASC) {
                        onSortChange?.([]);
                      } else {
                        onSortChange?.([{ field: header.column.id, isASC }]);
                      }
                    };

                    return (
                      <TableHead
                        key={header.id}
                        className={`border-b border-r border-t border-stroke-light ${
                          isSelectColumn ? "!w-[80px] !max-w-[80px] !min-w-[80px] px-1 sticky left-0 bg-white z-50" : "px-3"
                        }`}
                        style={{
                          color: 'var(--Text-Default, #495057)',
                          fontFamily: 'Satoshi',
                          fontSize: '13px',
                          fontStyle: 'normal',
                          fontWeight: '700',
                          lineHeight: 'normal'
                        }}
                      >
                        {isSelectColumn ? (
                          flexRender(header.column.columnDef.header, header.getContext())
                        ) : (
                          <div className="flex items-center group flex-1 justify-between relative">
                            <span className="flex-1">
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </span>
                            {canSort && (
                              <div className="flex items-center">
                                {sortInfo ? (
                                  sortInfo.isASC ? (
                                    <BsArrowUp className="ml-1 text-text-primary" />
                                  ) : (
                                    <BsArrowDown className="ml-1 text-text-primary" />
                                  )
                                ) : null}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="h-6 w-6 p-0 invisible group-hover:visible">
                                      <MdOutlineKeyboardArrowDown className="size-5 text-text-default" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() => handleSortToggle(true)}
                                      className={`cursor-pointer px-3 py-1 text-13 text-text-default flex items-center gap-2 group hover:!bg-primaryOpacity-10 ${
                                        isActiveAsc ? "!bg-primaryOpacity-10" : ""
                                      }`}
                                    >
                                      <BsArrowDown className={`text-text-default group-hover:text-text-primary rotate-180 ${isActiveAsc ? "text-text-primary" : ""}`} />
                                      <LightText2 
                                        text={isNumericField(header.column.id) ? "Sort 0 - 9" : "Sort A - Z"} 
                                        className={`group-hover:text-text-primary ${isActiveAsc ? "text-text-primary" : ""}`} 
                                      />
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleSortToggle(false)}
                                      className={`cursor-pointer px-3 py-2 text-13 text-text-default group hover:!bg-primaryOpacity-10 flex items-center gap-2 ${
                                        isActiveDesc ? "!bg-primaryOpacity-10" : ""
                                      }`}
                                    >
                                      <BsArrowDown className={`text-text-default group-hover:text-text-primary ${isActiveDesc ? "text-text-primary" : ""}`} />
                                      <LightText2 
                                        text={isNumericField(header.column.id) ? "Sort 9 - 0" : "Sort Z - A"} 
                                        className={`group-hover:text-text-primary ${isActiveDesc ? "text-text-primary" : ""}`} 
                                      />
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            )}
                          </div>
                        )}
                      </TableHead>
                    );
                  })
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Table Body Skeleton */}
              {Array.from({ length: 20 }).map((_, rowIdx) => (
                <TableRow key={rowIdx} className="animate-pulse">
                  {enableRowSelection && (
                    <TableCell className="!w-[80px] !max-w-[80px] !min-w-[80px] py-0.5 pl-1 border-b border-r border-stroke-light sticky left-0 bg-white">
                      <div className="h-4 w-full bg-gray-200 rounded" />
                    </TableCell>
                  )}
                  {!enableRowSelection && enableMoreBtn && (
                    <TableCell className="!w-[40px] !max-w-[40px] !min-w-[40px] 2xl:!w-[30px] 2xl:max-w-[30px] 2xl:min-!w-[30px] py-0.5 pl-1 border-b border-r border-stroke-light sticky left-0 bg-white">
                      <div className="h-4 w-full bg-gray-200 rounded" />
                    </TableCell>
                  )}
                  {columnsConfig.map((col, colIdx) => (
                    <TableCell
                      key={col.id || colIdx}
                      className={`px-3 font-normal !leading-normal !text-13 border-b border-r border-stroke-light ${columnClassName}`}
                    >
                      <div className="h-4 w-full bg-gray-200 rounded" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* pagination section - keep visible during loading */}
        {!hidePagination && (
          <div className="sticky bottom-0 bg-white border-t border-stroke-light z-10">
            <Pagination
              currentPage={currentPage}
              totalPages={totalCount ? Math.ceil(totalCount / perPage.value) : Math.ceil(table.getFilteredRowModel().rows.length / perPage.value)}
              perPage={perPage}
              perPageOptions={paginationConfig.perPageOptions!}
              onPageChange={(page: any) => {
                setCurrentPage(page);
                onPageChange?.(page);
              }}
              onPerPageChange={(count: any) => {
                setPerPage(count);
                setCurrentPage(1);
                onPerPageChange?.(count.value);
              }}
            />
          </div>
        )}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`${className} flex flex-col h-full`}>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="mb-1">
              <img src="/icons/error-state.svg" alt="Error" className="mx-auto w-[150px] h-[100px]" />
            </div>
            <div className="flex flex-col items-center justify-center">
              <LightText2 text="Something went wrong" className="text-lg font-semibold text-gray-800 mb-1" />
              {onRetry && (
                <Button variant="outline" onClick={onRetry} className="mt-2">
                  Try Again
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div className={`${className} flex flex-col h-full`}>
        <div className="flex-1 flex items-center justify-center">
          <EmptyList
            text={emptyStateText}
            icon={emptyStateIcon}
            className="h-full gap-y-4"
          />
        </div>
      </div>
    );
  }

  // Main table content
  return (
    <div className={`${className} flex flex-col h-full`}>
      <div className="flex-1 overflow-y-auto">
        <Table className="border-separate border-spacing-0 w-full">
          <TableHeader className="sticky top-0 z-30 bg-white whitespace-nowrap">
            <TableRow className="w-fit">
               {!enableRowSelection && enableMoreBtn && (
                 <TableHead className="!w-[40px] !max-w-[40px] !min-w-[40px] 2xl:!w-[30px] 2xl:max-w-[30px] 2xl:min-!w-[30px] border-b border-r border-t border-stroke-light sticky left-0 bg-white z-50" />
               )}
              {table.getHeaderGroups().map((headerGroup) =>
                headerGroup.headers.map((header) => {
                  const isSelectColumn = header.column.id === "select";
                  const canSort = enableSorting && !isSelectColumn && header.column.getCanSort();
                  const sortInfo = sortBy.find((s: any) => s.field === header.column.id);
                  const isActiveAsc = !!(sortInfo && sortInfo.isASC === true);
                  const isActiveDesc = !!(sortInfo && sortInfo.isASC === false);

                  const handleSortToggle = (isASC: boolean) => {
                    if (sortInfo && sortInfo.isASC === isASC) {
                      onSortChange?.([]);
                    } else {
                      onSortChange?.([{ field: header.column.id, isASC }]);
                    }
                  };

                  return (
                    <TableHead
                      key={header.id}
                      className={`border-b border-r border-t border-stroke-light ${
                        isSelectColumn ? "!w-[80px] !max-w-[80px] !min-w-[80px] px-1 sticky left-0 bg-white z-50" : "px-3"
                      }`}
                      style={{
                        color: 'var(--Text-Default, #495057)',
                        fontFamily: 'Satoshi',
                        fontSize: '13px',
                        fontStyle: 'normal',
                        fontWeight: '700',
                        lineHeight: 'normal'
                      }}
                    >
                      {isSelectColumn ? (
                        flexRender(header.column.columnDef.header, header.getContext())
                      ) : (
                        <div className="flex items-center group flex-1 justify-between relative">
                          <span className="flex-1">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                          {canSort && (
                            <div className="flex items-center">
                              {sortInfo ? (
                                sortInfo.isASC ? (
                                  <BsArrowUp className="ml-1 text-text-primary" />
                                ) : (
                                  <BsArrowDown className="ml-1 text-text-primary" />
                                )
                              ) : null}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" className="h-6 w-6 p-0 invisible group-hover:visible">
                                    <MdOutlineKeyboardArrowDown className="size-5 text-text-default" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => handleSortToggle(true)}
                                    className={`cursor-pointer px-3 py-1 text-13 text-text-default flex items-center gap-2 group hover:!bg-primaryOpacity-10 ${
                                      isActiveAsc ? "!bg-primaryOpacity-10" : ""
                                    }`}
                                  >
                                    <BsArrowDown className={`text-text-default group-hover:text-text-primary rotate-180 ${isActiveAsc ? "text-text-primary" : ""}`} />
                                    <LightText2 
                                      text={isNumericField(header.column.id) ? "Sort 0 - 9" : "Sort A - Z"} 
                                      className={`group-hover:text-text-primary ${isActiveAsc ? "text-text-primary" : ""}`} 
                                    />
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleSortToggle(false)}
                                    className={`cursor-pointer px-3 py-2 text-13 text-text-default group hover:!bg-primaryOpacity-10 flex items-center gap-2 ${
                                      isActiveDesc ? "!bg-primaryOpacity-10" : ""
                                    }`}
                                  >
                                    <BsArrowDown className={`text-text-default group-hover:text-text-primary ${isActiveDesc ? "text-text-primary" : ""}`} />
                                    <LightText2 
                                      text={isNumericField(header.column.id) ? "Sort 9 - 0" : "Sort Z - A"} 
                                      className={`group-hover:text-text-primary ${isActiveDesc ? "text-text-primary" : ""}`} 
                                    />
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          )}
                        </div>
                      )}
                    </TableHead>
                  );
                })
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row: any) => {
                return (
                  <TableRow key={row.id || row.index} className="group relative" data-state={row.getIsSelected() && "selected"}>
                     {enableMoreBtn ? (
                       <TableCell className="!w-[40px] !max-w-[40px] !min-w-[40px] 2xl:!w-[30px] 2xl:max-w-[30px] 2xl:min-!w-[30px] py-0.5 pl-1 border-b border-r border-stroke-light sticky left-0 bg-white">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4 text-text-default" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            {dropdownOptions
                              .filter((option) => (typeof option.show === "function" ? option.show(row.original) : true))
                              .map((option, index) => (
                                <DropdownMenuItem
                                  key={index}
                                  onClick={() => option.onClickFun(row.original)}
                                  className={`cursor-pointer px-3 py-2 text-13 ${
                                    option.type === "remove"
                                      ? "text-text-error data-[highlighted]:text-text-error"
                                      : "text-text-default data-[highlighted]:text-text-primary"
                                  } data-[highlighted]:bg-primaryOpacity-10 focus:bg-primaryOpacity-10 focus:text-text-primary`}
                                >
                                  {option.label}
                                </DropdownMenuItem>
                              ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                     ) : !enableRowSelection && enableMoreBtn && (
                     <TableCell className="!w-[40px] !max-w-[40px] !min-w-[40px] 2xl:!w-[30px] 2xl:max-w-[30px] 2xl:min-!w-[30px] py-0.5 pl-1 border-b border-r border-stroke-light sticky left-0 bg-white">
                      <div className="flex items-center justify-end"></div>
                      </TableCell>)}
                    {row.getVisibleCells().map((cell: any) => {
                      const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());
                      
                      return (
                        <TableCell
                          key={cell.id}
                          className={
                            cell.column.id === "select"
                              ? "!w-[80px] !max-w-[80px] !min-w-[80px] py-0.5 pl-1 border-b border-r border-stroke-light sticky left-0 bg-white"
                              : `px-3 font-normal !leading-normal !text-13 border-b border-r border-stroke-light ${columnClassName}`
                          }
                        >
                          <TruncatedToolTipChildren>
                            {cell.column.id === "select" ? (
                              cellContent
                            ) : cell.column.id === "added_ts" || cell.column.id === "updatedTs" ? (
                              <span>{moment(row.original[cell.column.id]).format("DD-MMM-YYYY h:mm A")}</span>
                            ) : (
                              cellContent
                            )}
                          </TruncatedToolTipChildren>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={finalColumns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* pagination section */}
      {!hidePagination && (
        <div className="sticky bottom-0 bg-white border-t border-stroke-light z-10">
          <Pagination
            currentPage={currentPage}
            totalPages={totalCount ? Math.ceil(totalCount / perPage.value) : Math.ceil(table.getFilteredRowModel().rows.length / perPage.value)}
            perPage={perPage}
            perPageOptions={paginationConfig.perPageOptions!}
            onPageChange={(page: any) => {
              setCurrentPage(page);
              onPageChange?.(page);
            }}
            onPerPageChange={(count: any) => {
              setPerPage(count);
              setCurrentPage(1);
              onPerPageChange?.(count.value);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default PaymentHistoryTable;

